/**
 * Alle sichtbaren Texte an einer Stelle.
 *
 * WARUM: Mehrere Regeln aus dem Designbriefing sind RECHTLICHER Natur, nicht
 * gestalterischer. Wenn die Texte über zwanzig Komponenten verstreut sind,
 * überlebt keine davon die dritte Änderung. Hier stehen sie zusammen und
 * lassen sich in einem Durchgang prüfen.
 *
 * Die Regeln, die hier gelten (siehe docs/OFFLINE-BUSINESS-BRIEFING.md):
 *
 *   - "Buchhaltung" NIE als eigenständige Leistungsbezeichnung, Rolle oder
 *     Überschrift. In Deutschland liest sich das als geschützte
 *     Berufsbezeichnung. Erlaubt nur im Fliesstext mit Einschränkung:
 *     "vorbereitende Arbeiten für Ihren Steuerberater". Dasselbe gilt für
 *     Steuerberater, Rechtsberatung, Lohnbuchhaltung.
 *   - Die Leistungsliste ist NIE abschliessend - jeder Block endet mit einer
 *     Öffnung.
 *   - Keine feste Reaktionszeit zusagen. Nie "innerhalb von 24 Stunden".
 *   - Preise sind freibleibend; der Hinweis hängt an jedem Angebotsdokument.
 *   - Ansprache immer förmlich (Sie). Sammelmaterial spricht als "wir",
 *     persönliche Korrespondenz als "ich".
 *   - Kein Emoji, kein Ausrufezeichen, keine Superlative.
 *
 * Die Texte selbst stammen aus dem Designset bzw. der Live-Seite und sind
 * nicht erfunden.
 */

export const firma = {
  name: 'Manuel Schneider Dienstleistungen',
  inhaber: 'Manuel Schneider',
  strasse: 'Mummenhofstr. 44',
  ort: '29229 Celle',
  telefon: '0176 36233983',
  web: 'www.mlaenu.de',
  steuernummer: '17/141/06257',
  rechtsform: 'Einzelunternehmen',
  /** Öffentlich, für alles Vervielfältigte: Flyer, Website, Impressum, Briefbogen. */
  mailAllgemein: 'hello@mlaenu.de',
  /** Nur bei Eins-zu-eins-Kontakt: Visitenkarte, Informationsblock eines Briefs. */
  mailPersoenlich: 'manuel@mlaenu.de',
  claim: 'damit Sie sich aufs Wesentliche konzentrieren können',
} as const;

export const start = {
  auszeichnung: 'Entlastung für Ihr Unternehmen',
  titel:
    'Wir kümmern uns um Ihr Backoffice, damit Sie sich auf Ihr Kerngeschäft konzentrieren können.',
  vorspann:
    'Wir übernehmen, was bei Ihnen liegen bleibt — vom E-Mail-Management über die Vorbereitung Ihrer Unterlagen bis zur Pflege Ihrer Webseite.',
} as const;

/**
 * Drei Leistungen. Der mittlere Titel heisst bewusst "Belege & Unterlagen"
 * und nicht "Buchhaltung" - siehe Kopfkommentar.
 */
export const leistungen = [
  {
    titel: 'E-Mail-Management',
    text: 'Schnelle, kompetente Beantwortung und Sortierung Ihrer täglichen Korrespondenz.',
  },
  {
    titel: 'Belege & Unterlagen',
    text: 'Vorbereitende Arbeiten und strukturierte Belege für Ihren Steuerberater.',
  },
  {
    titel: 'Backoffice & Organisation',
    text: 'Allgemeine Backoffice-Tätigkeiten zur spürbaren Entlastung in Ihrem Arbeitsalltag.',
  },
] as const;

/** Pflicht-Öffnung nach jedem Leistungsblock. */
export const nichtAbschliessend = {
  titel: 'Nicht dabei?',
  text:
    'Diese Liste ist nicht abschließend. Wir übernehmen die Aufgaben, um die Sie uns bitten — ' +
    'sprechen Sie uns einfach an.',
} as const;

export const kontakt = {
  titel: 'Lassen Sie uns sprechen',
  text: 'Wie können wir Ihr Unternehmen entlasten? Schreiben Sie uns eine Nachricht.',
  /** Keine feste Frist zusagen. */
  zusage: 'Wir melden uns schnellstmöglich bei Ihnen.',
  absenden: 'Nachricht senden',
  datenschutzHinweis: 'Ich habe die Datenschutzerklärung gelesen.',
} as const;

/** Angebot, Stand Mai 2026; Kilometersätze im Juli 2026 korrigiert. */
export const pakete = [
  {
    name: 'Basic',
    preis: '380,00 €',
    takt: 'monatlich',
    zeilen: [
      '10 Abrechnungsstunden im Monat',
      '0,38 € je Kilometer',
      'Abrechnung im 15-Minuten-Takt',
      'Keine Übernahme unverbrauchter Stunden',
      '60 Tage Kündigungsfrist zum Monatsende',
    ],
  },
  {
    name: 'Pro',
    preis: '730,00 €',
    takt: 'monatlich',
    zeilen: [
      '20 Abrechnungsstunden im Monat',
      '0,36 € je Kilometer',
      'Priorisierte Bearbeitung',
      'Bis zu 5 unverbrauchte Stunden in den Folgemonat',
      '60 Tage Kündigungsfrist zum Monatsende',
    ],
  },
  {
    name: 'Long',
    preis: '1.030,00 €',
    takt: 'quartalsweise',
    zeilen: [
      '30 Abrechnungsstunden im Quartal',
      '0,32 € je Kilometer',
      'Bevorzugte Bearbeitung',
      'Bis zu 7,5 unverbrauchte Stunden in den Folgemonat',
      '14 Tage Kündigungsfrist',
    ],
  },
  {
    name: 'Flex',
    preis: '40,00 €',
    takt: 'je Stunde',
    zeilen: ['Einzelabrechnung nach den Bedingungen von Basic', 'Ohne Laufzeit'],
  },
] as const;

export const empfehlung = {
  titel: 'Kunden werben Kunden',
  text:
    'Empfehlen Sie uns weiter: Sie und der geworbene Kunde erhalten je 10 % Rabatt ' +
    'auf die nächste Abrechnungsperiode.',
} as const;

/**
 * Kleingedrucktes ohne Fussnotenzeichen. Die Vorlage benutzte hochgestellte
 * Ziffern; hier steht jede Bedingung in der Zeile, die sie betrifft, und unten
 * nur das, was wirklich für alles gilt.
 */
export const kleingedrucktes = [
  'Alle Preise verstehen sich freibleibend und unverbindlich.',
  'Nicht verbrauchte Stunden verfallen, soweit oben nichts anderes angegeben ist.',
  `${firma.name} · ${firma.inhaber} · ${firma.strasse} · ${firma.ort} · ${firma.mailAllgemein}`,
] as const;
